import { useCallback, useEffect } from 'react'

type DismissPopupMessage = {
  messageType: 'popup-dismiss'
  popupType: PopupType
  action: 'dismiss' | 'getStatus'
}

type DismissStatusMessage = {
  messageType: 'popup-dismiss-status'
  status: DismissStatus
}

export type PopupType = 'newsletter' | 'signup'
const sessionStorageKey = 'popup-dismiss-status'

export type DismissStatus = {
  [key in PopupType]?: boolean
}

export function isDismissPopupMessage(message: any): message is DismissPopupMessage {
  return message?.messageType === 'popup-dismiss' && 'popupType' in message
}

export const useDismissGamPopup = () => {
  const getDismissStatus = useCallback((): DismissStatus => {
    const string = sessionStorage.getItem(sessionStorageKey)
    if (string === null || string === undefined || string === '') {
      return {}
    }
    const status = JSON.parse(string) as DismissStatus
    return status
  }, [])

  const dismissPopup = useCallback(
    (type: PopupType) => {
      const status = getDismissStatus()
      sessionStorage.setItem(sessionStorageKey, JSON.stringify({ ...status, [type]: true }))
    },
    [getDismissStatus]
  )

  const sendDismissStatus = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const message: DismissStatusMessage = {
      messageType: 'popup-dismiss-status',
      status: getDismissStatus(),
    }

    const iframes = Array.from(document.querySelectorAll('iframe'))
    const googleAdsIframes = iframes.filter((iframe) => iframe.id.startsWith('google_ads_iframe_'))
    googleAdsIframes.forEach((iframe) => {
      iframe.contentWindow?.postMessage(message, window.location.origin)
    })
  }, [getDismissStatus])

  const parseMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return
      }
      const { data } = event
      if (isDismissPopupMessage(data)) {
        if (data.action === 'getStatus') {
          sendDismissStatus()
          return
        } else if (data.action === 'dismiss') {
          dismissPopup(data.popupType)
          return
        }
      }
    },
    [dismissPopup, sendDismissStatus]
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    window.addEventListener('message', parseMessage)
    return () => {
      window.removeEventListener('message', parseMessage)
    }
  }, [sendDismissStatus, dismissPopup, parseMessage])

  return { dismissPopup, sendDismissStatus }
}
